import { Button, toast } from "@shuttlerock/mui-components";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { queryClient } from "../../data/queryClient";
import { useTranslation } from "../../i18n";
import { cyrb53 } from "./cyrb53";

export type AutoUpdateProps = {
  fetchInterval: number;
};

// The one notice in the app: a new build is waiting and there is no other place
// to say so. It stays until the user updates or dismisses it.
const AUTO_UPDATE_TOAST_KEY = "auto-update";

const reloadWithUpdatedFlag = () => {
  const url = new URL(window.location.toString());
  url.searchParams.set("updated", "true");
  window.history.replaceState(null, "", url.toString());
  window.location.reload();
};

export function AutoUpdate({ fetchInterval }: AutoUpdateProps): null {
  const { t } = useTranslation("autoUpdate");
  const [notified, setNotified] = useState(false);
  const enabled = !notified && typeof window !== "undefined" && !!window.location?.reload;

  useQuery(
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    {
      queryKey: ["auto-update"],
      refetchInterval: fetchInterval,
      enabled,
      async queryFn() {
        const somethingChanged = await checkForChanges([
          { name: "index", url: "/index.html" },
          { name: "config-host", url: "/config/env-host-config.js" },
          { name: "config", url: "/config/env-config.js" },
        ]);

        if (!somethingChanged) {
          return false;
        }

        toast.notice(t("ready"), {
          key: AUTO_UPDATE_TOAST_KEY,
          action: (
            <Button variant="text" color="inherit" size="small" onClick={reloadWithUpdatedFlag}>
              {t("updateNow")}
            </Button>
          ),
        });
        setNotified(true);

        return true;
      },
    },
    queryClient,
  );

  return null;
}

type Entrypoint = {
  name: string;
  url: string;
};

async function checkForChanges(entrypoints: Entrypoint[]) {
  const changes = await Promise.all(entrypoints.map(hasChanged));
  return changes.some((x) => x);
}

const hashMap = new Map<string, number>();

async function hasChanged({ name, url }: Entrypoint) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch entrypoint from ${url}`);
    }

    const existingHash = hashMap.get(name);
    const latestHash = cyrb53(await response.text());
    hashMap.set(name, latestHash);

    if (!existingHash) {
      // First (successful) fetch, store the current hash and move on
      return false;
    }

    return latestHash !== existingHash;
  } catch {
    // do nothing
    return false;
  }
}
